import React, { useState } from "react";
import styled from "styled-components";
import {
  useGetRespectAdminOverviewQuery,
  useGetRespectAdminBreakdownQuery,
  useAdjustRespectMutation,
  useInitializeRespectMutation,
  useRecalculatePositionsMutation,
  AdminUserEntry,
} from "../../services/rtkApi/respectApi";
import { useConfirm } from "../../ui/components/ConfirmDialog";

const RespectAdminPanel: React.FC = () => {
  const confirm = useConfirm();
  const [isOpen, setIsOpen] = useState(false);
  const [adjustUserId, setAdjustUserId] = useState("");
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const { data: overview, isLoading, refetch } = useGetRespectAdminOverviewQuery(undefined, {
    skip: !isOpen,
  });
  const { data: breakdown, isLoading: isBreakdownLoading } = useGetRespectAdminBreakdownQuery(
    selectedUserId ?? "",
    {
      skip: !isOpen || !selectedUserId,
    }
  );

  const [adjustRespect, { isLoading: isAdjusting }] = useAdjustRespectMutation();
  const [initializeRespect, { isLoading: isInitializing }] = useInitializeRespectMutation();
  const [recalculatePositions, { isLoading: isRecalculating }] = useRecalculatePositionsMutation();

  const handleAdjust = async () => {
    const delta = parseFloat(adjustDelta);
    if (!adjustUserId || isNaN(delta) || delta === 0 || !adjustReason.trim()) {
      setActionMessage("fill in user id, a non-zero delta, and a reason.");
      return;
    }
    try {
      const result = await adjustRespect({ userId: adjustUserId, delta, reason: adjustReason }).unwrap();
      setActionMessage(result.message);
      setAdjustUserId("");
      setAdjustDelta("");
      setAdjustReason("");
      refetch();
    } catch {
      setActionMessage("adjust failed. check console.");
    }
  };

  const handleInitialize = async () => {
    const ok = await confirm({
      title: "Rebuild respect?",
      message: "This resets respect, badges, events, and reputation caches before replaying the rules.",
      confirmLabel: "Rebuild",
      tone: "danger",
    });
    if (!ok) return;

    try {
      const result = await initializeRespect().unwrap();
      setActionMessage(result.message);
      refetch();
    } catch {
      setActionMessage("initialization failed. check console.");
    }
  };

  const handleRecalculate = async () => {
    try {
      const result = await recalculatePositions().unwrap();
      setActionMessage(result.message || "positions recalculated.");
      refetch();
    } catch {
      setActionMessage("recalculation failed. check console.");
    }
  };

  return (
    <PanelWrapper>
      <PanelToggle onClick={() => setIsOpen((v) => !v)}>
        {isOpen ? "▾" : "▸"} respect admin {isOpen ? "" : "(hidden)"}
      </PanelToggle>

      {isOpen && (
        <PanelBody>
          <Section>
            <SectionTitle>actions</SectionTitle>
            <ButtonRow>
              <ActionButton onClick={handleInitialize} disabled={isInitializing}>
                {isInitializing ? "running..." : "initialize respect (reset + replay)"}
              </ActionButton>
              <ActionButton onClick={handleRecalculate} disabled={isRecalculating}>
                {isRecalculating ? "running..." : "recalculate positions"}
              </ActionButton>
            </ButtonRow>
            {actionMessage && <ActionMessage>{actionMessage}</ActionMessage>}
          </Section>

          <Section>
            <SectionTitle>adjust user respect</SectionTitle>
            <AdjustForm>
              <FormRow>
                <Label>user id</Label>
                <Input
                  type="text"
                  value={adjustUserId}
                  onChange={(e) => setAdjustUserId(e.target.value)}
                  placeholder="mongodb objectid"
                />
              </FormRow>
              <FormRow>
                <Label>delta</Label>
                <Input
                  type="number"
                  value={adjustDelta}
                  onChange={(e) => setAdjustDelta(e.target.value)}
                  placeholder="+1.5 or -0.5"
                  step="0.01"
                />
              </FormRow>
              <FormRow>
                <Label>reason</Label>
                <Input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="brief reason for audit trail"
                />
              </FormRow>
              <ActionButton onClick={handleAdjust} disabled={isAdjusting}>
                {isAdjusting ? "adjusting..." : "apply adjustment"}
              </ActionButton>
            </AdjustForm>
          </Section>

          <Section>
            <SectionTitle>pool state</SectionTitle>
            {overview?.data?.pool && (
              <PoolGrid>
                <PoolCard>
                  <PoolLabel>total supply</PoolLabel>
                  <PoolValue>{overview.data.pool.totalSupply.toFixed(2)}</PoolValue>
                </PoolCard>
                <PoolCard>
                  <PoolLabel>distributed</PoolLabel>
                  <PoolValue>{overview.data.pool.distributed.toFixed(2)}</PoolValue>
                </PoolCard>
                <PoolCard>
                  <PoolLabel>available</PoolLabel>
                  <PoolValue>{overview.data.pool.available.toFixed(2)}</PoolValue>
                </PoolCard>
              </PoolGrid>
            )}
          </Section>

          <Section>
            <SectionTitle>
              position overview
              {overview?.data && (
                <small> — {overview.data.totalUsers} users · calculated {new Date(overview.data.calculatedAt).toLocaleTimeString()}</small>
              )}
            </SectionTitle>

            {overview?.data?.tierBreakdown && (
              <TierBreakdown>
                {overview.data.tierBreakdown.map((t) => (
                  <TierPill key={t.tier.id}>
                    {t.tier.badge} {t.tier.name}: {t.userCount} users · {t.tier.threshold.toFixed(2)}+
                  </TierPill>
                ))}
              </TierBreakdown>
            )}

            {isLoading && <StatusText>loading...</StatusText>}

            {overview?.data?.users && (
              <TableScrollWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>rank</Th>
                      <Th>username</Th>
                      <Th>position</Th>
                      <Th>totalEarned</Th>
                      <Th>balance</Th>
                      <Th>locked</Th>
                      <Th>badges</Th>
                      <Th>userId</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.data.users.map((u: AdminUserEntry) => (
                      <Tr
                        key={u.userId}
                        onClick={() => setSelectedUserId((current) => (current === u.userId ? null : u.userId))}
                      >
                        <Td>{u.rank}</Td>
                        <Td><strong>{u.username}</strong></Td>
                        <Td>
                          <PositionCell>
                            {u.position.badge} {u.position.name}
                          </PositionCell>
                        </Td>
                        <Td>{u.totalEarned.toFixed(2)}</Td>
                        <Td>{u.balance.toFixed(2)}</Td>
                        <Td>{u.locked.toFixed(2)}</Td>
                        <Td>{u.badgeCount}</Td>
                        <Td><UserId>{u.userId}</UserId></Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScrollWrap>
            )}
          </Section>

          <Section>
            <SectionTitle>
              respect breakdown
              {breakdown?.data && <small> — {breakdown.data.username}</small>}
            </SectionTitle>
            {!selectedUserId && <StatusText>click a user row to inspect how their respect was earned.</StatusText>}
            {isBreakdownLoading && <StatusText>loading breakdown...</StatusText>}
            {selectedUserId && breakdown?.data?.breakdown?.length === 0 && (
              <StatusText>no respect events found for this user.</StatusText>
            )}
            {breakdown?.data?.breakdown?.length ? (
              <BreakdownList>
                {breakdown.data.breakdown.map((entry) => (
                  <BreakdownItem key={entry.type}>
                    <BreakdownType>{entry.type}</BreakdownType>
                    <BreakdownMeta>
                      {entry.total.toFixed(2)} total · {entry.count} events
                    </BreakdownMeta>
                  </BreakdownItem>
                ))}
              </BreakdownList>
            ) : null}
          </Section>
        </PanelBody>
      )}
    </PanelWrapper>
  );
};

const PanelWrapper = styled.div`
  margin: 12px 0;
  border: 2px dashed #a89fd8;
  border-radius: 4px;
  background: #f0ecff;
`;

const PanelToggle = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 10px 14px;
  font-family: monospace;
  font-size: 0.85rem;
  color: #5c4a8a;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background: #e4deff;
  }
`;

const PanelBody = styled.div`
  padding: 12px 14px;
  border-top: 1px dashed #a89fd8;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 700px) {
    padding: 10px 12px;
    gap: 14px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-family: monospace;
  font-size: 0.8rem;
  font-weight: 700;
  color: #5c4a8a;
  text-transform: lowercase;

  small {
    font-weight: 400;
    color: #8877b5;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: #5c4a8a;
  color: white;
  border: none;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #4a3870;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionMessage = styled.div`
  font-family: monospace;
  font-size: 0.8rem;
  color: #2d5a27;
  background: #d4edda;
  padding: 6px 10px;
  border-radius: 3px;
`;

const PoolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
`;

const PoolCard = styled.div`
  padding: 10px;
  border: 1px solid #d8d0f0;
  border-radius: 4px;
  background: white;
`;

const PoolLabel = styled.div`
  font-family: monospace;
  font-size: 0.72rem;
  color: #8877b5;
  text-transform: lowercase;
`;

const PoolValue = styled.div`
  font-family: monospace;
  font-size: 1rem;
  font-weight: 700;
  color: #4a3870;
`;

const AdjustForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 480px;
  width: 100%;
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Label = styled.label`
  font-family: monospace;
  font-size: 0.8rem;
  color: #5c4a8a;
  width: 60px;
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding: 4px 8px;
  font-family: monospace;
  font-size: 0.8rem;
  border: 1px solid #a89fd8;
  border-radius: 3px;
  background: white;
  box-sizing: border-box;

  &:focus {
    outline: 1px solid #5c4a8a;
  }
`;

const TierBreakdown = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const TierPill = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  padding: 3px 8px;
  background: #e4deff;
  border-radius: 999px;
  color: #5c4a8a;
  border: 1px solid #a89fd8;
`;

const StatusText = styled.div`
  font-family: monospace;
  font-size: 0.8rem;
  color: #8877b5;
`;

const TableScrollWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: monospace;
  font-size: 0.75rem;
  min-width: 640px;
`;

const Th = styled.th`
  text-align: left;
  padding: 4px 8px;
  border-bottom: 2px solid #a89fd8;
  color: #5c4a8a;
  font-weight: 700;
  white-space: nowrap;
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background: #e4deff;
  }
`;

const Td = styled.td`
  padding: 4px 8px;
  border-bottom: 1px solid #d8d0f0;
  vertical-align: middle;
`;

const PositionCell = styled.span`
  font-weight: 700;
  color: #4a3870;
`;

const UserId = styled.span`
  color: #8877b5;
  font-size: 0.7rem;
  user-select: all;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BreakdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #d8d0f0;
  border-radius: 4px;
  background: white;
`;

const BreakdownType = styled.span`
  font-family: monospace;
  font-size: 0.78rem;
  color: #4a3870;
  font-weight: 700;
`;

const BreakdownMeta = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  color: #8877b5;
`;

export default RespectAdminPanel;
